import { useCallback, useEffect, useState } from 'react';
import type {
  BuildSchedule,
  AvoidSchedule,
  CompletionHistoryMonthSummaryView,
  CompletionHistoryMonthView,
  TargetAdjustment,
  YearMonth,
} from '@contracts';
import { useDomainApi, useDomainVersion } from '@ui/shared/domain/DomainApiContext';
import { ChevronLeft, ChevronRight } from '@ui/shared/icons';
import { SkeletonBlock } from '@ui/shared/primitives/Skeleton';
import { formatMonthAbbrev, formatMonthYear, formatShortDate, localIsoDate, shiftYearMonth } from '@ui/shared/format/date';

/**
 * Completion History — desktop-only (completion-history.md v1.0). Month-
 * primary view (§1.3) with a navigational-only year strip above it (§1.2),
 * four calm category groups (§2), explicit empty/loading/error states (§3).
 * Read-only: no editing affordance of any kind on this screen (§7).
 *
 * "History" is deliberately the ONLY word this screen's own chrome uses for
 * itself, per the spec's naming-disambiguation reasoning (§0) — the header
 * framing line and the calm, non-log-like composition do the rest of that
 * work.
 */
export function CompletionHistoryScreenDesktop({
  onOpenObjective,
}: {
  readonly onOpenObjective: (objectiveId: string) => void;
}) {
  const api = useDomainApi();
  const version = useDomainVersion();

  const [currentMonth, setCurrentMonth] = useState<YearMonth | null>(null);
  const [monthView, setMonthView] = useState<CompletionHistoryMonthView | null>(null);
  const [yearSummary, setYearSummary] = useState<readonly CompletionHistoryMonthSummaryView[] | null>(null);
  const [loadError, setLoadError] = useState(false);

  const today = localIsoDate();
  const thisMonth = today.slice(0, 7) as YearMonth;

  const load = useCallback(
    async (month: YearMonth) => {
      try {
        const [view, summary] = await Promise.all([
          api.getCompletionHistory(month),
          api.getCompletionHistoryYearSummary(thisMonth),
        ]);
        setMonthView(view);
        setYearSummary(summary);
        setLoadError(false);
      } catch {
        setLoadError(true);
      }
    },
    [api, thisMonth],
  );

  // Mount-only: picks the initial month (today's). Deliberately NOT
  // re-run on every `version` bump — background sync activity (periodic
  // retry, ADR-0004) also flows through the same `subscribe`/`version`
  // channel every other domain-version-driven effect in this codebase
  // uses, and re-deriving "month = currentMonth ?? thisMonth" on every one
  // of those bumps risked a real bug: a `version` bump landing between a
  // month-nav tap's `setCurrentMonth` and React's next commit could still
  // read a stale `currentMonth` closure and silently snap the view back to
  // the current month mid-navigation. Splitting into two effects (mount-only
  // vs. a version-driven RE-LOAD of whatever month is already selected)
  // removes that race entirely: this effect never reads or writes
  // `currentMonth` from a `version` dependency, only from its own one-time run.
  useEffect(() => {
    const month = thisMonth;
    setCurrentMonth(month);
    void load(month);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-fetches the CURRENTLY SELECTED month's data whenever the domain log
  // advances (a real data change, e.g. completing an action while this
  // screen is open) — but never changes WHICH month is selected. Guarded on
  // `currentMonth !== null` so it doesn't double-fire against the mount
  // effect above.
  useEffect(() => {
    if (currentMonth === null) return;
    void load(currentMonth);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version]);

  const goToMonth = useCallback(
    (month: YearMonth) => {
      setCurrentMonth(month);
      void load(month);
    },
    [load],
  );

  const earliestMonth = yearSummary && yearSummary.length > 0 ? yearSummary[0]?.month : undefined;
  const atEarliest = !!currentMonth && !!earliestMonth && currentMonth <= earliestMonth;
  const atLatest = !!currentMonth && currentMonth >= thisMonth;

  return (
    <main aria-label="History" style={{ padding: 'var(--space-8) var(--layout-page-margin-desktop) var(--space-16)', maxWidth: '1040px', margin: '0 auto' }}>
      <header>
        <h1 style={{ margin: 0, fontSize: 'var(--type-display-size)', fontWeight: 'var(--type-display-weight)', color: 'var(--text-primary)' }}>
          History
        </h1>
        <p style={{ marginTop: 'var(--space-2)', fontSize: 'var(--type-body-size)', color: 'var(--text-secondary)' }}>
          What you got done, month by month.
        </p>
      </header>

      {loadError ? (
        <ErrorState onRetry={() => currentMonth && void load(currentMonth)} />
      ) : !monthView || !yearSummary || !currentMonth ? (
        <div style={{ marginTop: 'var(--layout-desktop-panel-gap)' }}>
          <SkeletonBlock height={44} />
          <div style={{ height: 'var(--space-4)' }} />
          <SkeletonBlock height={32} width={220} />
          <div style={{ height: 'var(--space-4)' }} />
          <SkeletonBlock height={180} />
        </div>
      ) : (
        <>
          <div style={{ marginTop: 'var(--layout-desktop-panel-gap)' }}>
            <YearStrip months={yearSummary} currentMonth={currentMonth} onSelect={goToMonth} />
          </div>

          <div style={{ marginTop: 'var(--layout-desktop-panel-gap)' }}>
            <MonthNavHeader
              month={currentMonth}
              atEarliest={atEarliest}
              atLatest={atLatest}
              onPrev={() => goToMonth(shiftYearMonth(currentMonth, -1) as YearMonth)}
              onNext={() => goToMonth(shiftYearMonth(currentMonth, 1) as YearMonth)}
            />
          </div>

          <div style={{ marginTop: 'var(--layout-desktop-panel-gap)' }}>
            <MonthContent view={monthView} onOpenObjective={onOpenObjective} />
          </div>
        </>
      )}
    </main>
  );
}

function YearStrip({
  months,
  currentMonth,
  onSelect,
}: {
  readonly months: readonly CompletionHistoryMonthSummaryView[];
  readonly currentMonth: YearMonth;
  readonly onSelect: (month: YearMonth) => void;
}) {
  return (
    <div
      role="group"
      aria-label="Year"
      style={{ display: 'flex', gap: 'var(--space-2)', overflowX: 'auto', paddingBottom: 'var(--space-1)' }}
    >
      {months.map((m) => {
        const isCurrent = m.month === currentMonth;
        return (
          <button
            key={m.month}
            type="button"
            onClick={() => onSelect(m.month)}
            aria-label={`${formatMonthYear(m.month)}, ${m.hasActivity ? 'has activity' : 'no activity'}`}
            style={{
              flex: 'none',
              minHeight: 'var(--touch-target-min)',
              padding: '0 var(--space-3)',
              borderRadius: 'var(--radius-md)',
              background: m.hasActivity ? 'var(--surface-hover)' : 'transparent',
              border: isCurrent ? '1px solid var(--hue-primary)' : '1px solid var(--border-subtle)',
              color: isCurrent ? 'var(--hue-primary)' : m.hasActivity ? 'var(--text-primary)' : 'var(--text-muted)',
              fontSize: 'var(--type-label-size)',
              fontWeight: 600,
            }}
          >
            {formatMonthAbbrev(m.month)}
          </button>
        );
      })}
    </div>
  );
}

function MonthNavHeader({
  month,
  atEarliest,
  atLatest,
  onPrev,
  onNext,
}: {
  readonly month: YearMonth;
  readonly atEarliest: boolean;
  readonly atLatest: boolean;
  readonly onPrev: () => void;
  readonly onNext: () => void;
}) {
  const prevMonth = shiftYearMonth(month, -1);
  const nextMonth = shiftYearMonth(month, 1);
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--space-3)' }}>
      <button
        type="button"
        onClick={onPrev}
        disabled={atEarliest}
        aria-disabled={atEarliest}
        aria-label={`Previous month: ${formatMonthYear(prevMonth)}`}
        style={{
          width: 'var(--touch-target-min)',
          height: 'var(--touch-target-min)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: atEarliest ? 0.4 : 1,
        }}
      >
        <ChevronLeft size={16} color="var(--text-muted)" aria-hidden="true" />
      </button>
      <h2 style={{ margin: 0, fontSize: 'var(--type-heading-size)', fontWeight: 'var(--type-heading-weight)', color: 'var(--text-primary)', minWidth: '180px', textAlign: 'center' }}>
        {formatMonthYear(month)}
      </h2>
      <button
        type="button"
        onClick={onNext}
        disabled={atLatest}
        aria-disabled={atLatest}
        aria-label={`Next month: ${formatMonthYear(nextMonth)}`}
        style={{
          width: 'var(--touch-target-min)',
          height: 'var(--touch-target-min)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: atLatest ? 0.4 : 1,
        }}
      >
        <ChevronRight size={16} color="var(--text-muted)" aria-hidden="true" />
      </button>
    </div>
  );
}

function MonthContent({
  view,
  onOpenObjective,
}: {
  readonly view: CompletionHistoryMonthView;
  readonly onOpenObjective: (objectiveId: string) => void;
}) {
  const empty =
    view.finishedObjectives.length === 0 &&
    view.objectiveActionsCompleted.length === 0 &&
    view.habitActivity.length === 0 &&
    view.realismAdjustments.length === 0;

  if (empty) {
    return (
      <p style={{ textAlign: 'center', fontSize: 'var(--type-body-size)', color: 'var(--text-secondary)', padding: 'var(--space-8) 0' }}>
        Nothing logged this month yet.
      </p>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--layout-desktop-panel-gap)' }}>
      {view.finishedObjectives.length > 0 && (
        <CategoryGroup label="Finished">
          {view.finishedObjectives.map((o) => (
            <button
              key={o.objectiveId}
              type="button"
              onClick={() => onOpenObjective(o.objectiveId)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-3)',
                width: '100%',
                textAlign: 'left',
                padding: 'var(--space-3) var(--space-4)',
                borderRadius: 'var(--radius-lg)',
                background: 'color-mix(in srgb, var(--hue-primary-vivid) 10%, var(--surface-raised))',
                border: '1px solid var(--border-subtle)',
              }}
            >
              <span aria-hidden="true" style={{ display: 'flex', flex: 'none' }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <circle cx="12" cy="12" r="11" fill="var(--hue-primary-vivid)" />
                  <path d="M17 8.5 10.5 15 7 11.5" stroke="var(--text-on-accent)" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
              <div>
                <div style={{ fontSize: 'var(--type-body-size)', fontWeight: 600, color: 'var(--text-primary)' }}>{o.title}</div>
                <div style={{ fontSize: 'var(--type-caption-size)', color: 'var(--text-secondary)', marginTop: 'var(--space-1)' }}>
                  {o.actionsTotal} {o.actionsTotal === 1 ? 'action' : 'actions'} · finished {formatShortDate(o.finishedOn)}.
                </div>
              </div>
            </button>
          ))}
        </CategoryGroup>
      )}

      {view.objectiveActionsCompleted.length > 0 && (
        <CategoryGroup label="Actions completed">
          {view.objectiveActionsCompleted.map((o) => (
            <Row key={o.objectiveId}>
              <span style={{ fontSize: 'var(--type-body-size)', color: 'var(--text-primary)' }}>{o.title}</span>
              <span className="num" style={{ fontSize: 'var(--type-body-size)', color: 'var(--text-secondary)' }}>
                {o.actionsCompleted} {o.actionsCompleted === 1 ? 'action completed' : 'actions completed'}
              </span>
            </Row>
          ))}
        </CategoryGroup>
      )}

      {view.habitActivity.length > 0 && (
        <CategoryGroup label="Habits">
          {view.habitActivity.map((h) => (
            <Row key={h.habitId}>
              <span style={{ fontSize: 'var(--type-body-size)', color: 'var(--text-primary)' }}>{h.name}</span>
              <span className="num" style={{ fontSize: 'var(--type-body-size)' }}>
                <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                  {h.habitKind === 'build' ? h.completions : h.cleanWeeks}
                </span>{' '}
                <span style={{ color: 'var(--text-secondary)' }}>
                  {h.habitKind === 'build'
                    ? h.completions === 1
                      ? 'session'
                      : 'sessions'
                    : h.cleanWeeks === 1
                      ? 'clean week'
                      : 'clean weeks'}
                </span>
              </span>
            </Row>
          ))}
        </CategoryGroup>
      )}

      {view.realismAdjustments.length > 0 && (
        <CategoryGroup label="Adjustments">
          {view.realismAdjustments.map((a) => (
            <p key={a.eventId} style={{ margin: 0, padding: 'var(--space-2) 0', fontSize: 'var(--type-body-size)', color: 'var(--text-secondary)' }}>
              {a.kind === 'objective_date' ? (
                <>
                  Moved &apos;<span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{a.title}</span>&apos; target from{' '}
                  {formatShortDate(a.previousTargetDate)} to {formatShortDate(a.nextTargetDate)}.
                </>
              ) : (
                <>
                  Adjusted &apos;<span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{a.name}</span>&apos; target from{' '}
                  {describeSchedule(a.adjustment, 'previous')} to {describeSchedule(a.adjustment, 'next')}.
                </>
              )}
            </p>
          ))}
        </CategoryGroup>
      )}
    </div>
  );
}

function describeSchedule(adjustment: TargetAdjustment, which: 'previous' | 'next'): string {
  if (adjustment.habitKind === 'build') {
    const schedule: BuildSchedule = adjustment[which];
    return schedule.kind === 'daily' ? 'daily' : `${schedule.targetPerWeek}× a week`;
  }
  const schedule: AvoidSchedule = adjustment[which];
  return schedule.limitPerWeek === 0 ? 'zero a week' : `at most ${schedule.limitPerWeek}× a week`;
}

function CategoryGroup({ label, children }: { readonly label: string; readonly children: React.ReactNode }) {
  return (
    <section>
      <div
        style={{
          fontSize: 'var(--type-label-size)',
          lineHeight: 'var(--type-label-line)',
          letterSpacing: 'var(--type-label-tracking)',
          textTransform: 'uppercase',
          color: 'var(--text-muted)',
          fontWeight: 600,
          marginBottom: 'var(--space-2)',
        }}
      >
        {label}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>{children}</div>
    </section>
  );
}

function Row({ children }: { readonly children: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 'var(--space-3)',
        padding: 'var(--space-3) var(--space-4)',
        borderRadius: 'var(--radius-lg)',
        background: 'var(--surface-raised)',
        border: '1px solid var(--border-subtle)',
      }}
    >
      {children}
    </div>
  );
}

function ErrorState({ onRetry }: { readonly onRetry: () => void }) {
  return (
    <div style={{ marginTop: 'var(--layout-desktop-panel-gap)', textAlign: 'center', padding: 'var(--space-8) 0' }}>
      <p style={{ fontSize: 'var(--type-body-size)', color: 'var(--text-secondary)' }}>
        Couldn&apos;t load your history. Your data is safe — try again.
      </p>
      <button
        type="button"
        onClick={onRetry}
        style={{
          marginTop: 'var(--space-3)',
          minHeight: 'var(--touch-target-min)',
          padding: '0 var(--space-5)',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--border-strong)',
          color: 'var(--text-secondary)',
          fontWeight: 500,
          fontSize: 'var(--type-body-size)',
        }}
      >
        Retry
      </button>
    </div>
  );
}
